/* eslint-disable no-unused-vars */
import { Step, Stepper } from "../../../../components/ui/stepper";
import { useEffect, useState } from "react";
import { SummaryStep } from "./SummaryStep";
import { RiskStepContent } from "./RiskStepContent";
import { useOrganization } from "../../../../lib/context/OrganisationContext";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { DateInputPicker } from "../../../../components/DateInputPicker";
import moment from "moment";
import UserSelect from "../../../../components/UserSelect";
import { formatSubcontractorName } from "../../../../lib/Misc";
import { Loader2 } from "lucide-react";
import YearPicker from "../../../../components/YearPicker";
import { API, fileUpload, get } from "../../../../config";
import { useCallback } from "react";

export default function AddEditRiskAssessment({
  fetchRiskData,
  toggleShowAddEditRisk,
  edit,
  sectionType,
  id,
}) {
  const { organizationData } = useOrganization();
  const [year, setYear] = useState(new Date().getFullYear());
  const [assessmentDate, setAssessmentDate] = useState(moment());
  // const [reviewDate, setReviewDate] = useState(moment().add(1, "year"));
  const [complianceManagerId, setComplianceManagerId] = useState();

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [stepsData, setStepsData] = useState([]);

  const [userLoading, setUserLoading] = useState(false);
  const [userListing, setUserListing] = useState([]);
  const [userList, setUserList] = useState([]);
  const [assessedBy, setAssessedBy] = useState("");
  const [analysis, setAnalysis] = useState();

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await get(`${API.GET_RISK_ASSESSMENT}/${id}`);
      const sectionData = data.data || [];

      setAssessmentDate(
        sectionData?.assessment_date
          ? moment(sectionData?.assessment_date, "DD-MM-YYYY")
          : null,
      );
      // setReviewDate(
      //   sectionData?.next_review_date
      //     ? moment(sectionData?.next_review_date, "DD-MM-YYYY")
      //     : null
      // );
      setYear(
        sectionData?.year ? moment(sectionData?.year, "YYYY").year() : null,
      );
      setAssessedBy(
        sectionData?.user
          ? sectionData?.user?.first_name
            ? sectionData?.user?.first_name + " " + sectionData?.user?.last_name
            : sectionData?.user?.last_name
          : "",
      );
      setAnalysis(data.analysis);

      setComplianceManagerId(
        sectionData?.compliance_manager_id
          ? sectionData?.compliance_manager_id
          : "",
      );
      // Example usage
      const transformed = sectionData?.risk_assessment_sections?.map(
        (sectionData) => ({
          id: sectionData?.section_id, // or use section.id if you prefer
          title:
            sectionData?.section?.title ||
            `Section ${sectionData?.section?.id}`,
          sub_title: sectionData?.section?.sub_title
            ? sectionData?.section?.sub_title
            : "",
          yes_color: sectionData?.section?.yes_color
            ? sectionData?.section?.yes_color
            : "#ff0000",
          no_color: sectionData?.section?.no_color
            ? sectionData?.section?.no_color
            : "#007718",
          questions: sectionData?.risk_assessment_section_answers?.map(
            (ans) => ({
              id: ans?.question?.id,
              label: ans?.question?.label,
              question: ans?.question?.question,
              options: ["Yes", "No"],
              answer: ans?.answer === 1 ? "Yes" : "No",
            }),
          ),
        }),
      );

      setSections(transformed);
      setStepsData([
        ...transformed?.map((section) => ({ label: section.title })),
        { label: "Summary" },
      ]);
    } catch (err) {
      console.error("Error fetching sections:", err);
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await get(
        `${API.GET_SECTIONS}?section_type=${sectionType}&organization_id=${organizationData?.organizationId}`,
      );
      const sectionData = data.data || [];

      const formatted = sectionData.map((sec) => ({
        id: sec.id,
        title: `${sec.title}`,
        sub_title: sec.sub_title ? sec.sub_title : "",
        yes_color: sec.yes_color ? sec.yes_color : "#ff0000",
        no_color: sec.no_color ? sec.no_color : "#007718",
        questions: sec.questions.map((q) => ({
          id: q.id,
          label: q.label,
          question: q.question,
          options: ["Yes", "No"],
          answer: null,
        })),
      }));

      setSections(formatted);

      setStepsData([
        ...formatted?.map((section) => ({ label: section.title })),
        { label: "Summary" },
      ]);
      if (id) {
        fetchData();
      }
    } catch (err) {
      console.error("Error fetching sections:", err);
      setSections([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData?.organizationId]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    if (userList) {
      let userTempListing = userList?.map((item) => ({
        ...item,
        value: item?.id,
        label: formatSubcontractorName(item),
      }));
      setUserListing(userTempListing || []);
    } else {
      setUserListing([]);
    }
  }, [userList]);

  const fetchAnalysis = useCallback(
    async (sections) => {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("organization_id", organizationData?.organizationId);

        formData.append("section_type", sectionType);
        for (let i = 0; i < sections?.length; i++) {
          let keyName = `section_${sections[i].id}`;
          formData.append(
            keyName,
            sections[i].questions.filter((q) => q.answer === "Yes").length,
          );
        }
        const { data } = await fileUpload(`${API.GET_ANALYSIS}`, formData);
        setAnalysis(data.data);
      } catch (err) {
        console.error("Error fetching sections:", err);
        setAnalysis([]);
      } finally {
        setLoading(false);
      }
    },
    [organizationData?.organizationId, sectionType],
  );

  const handleFinish = async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("organization_id", organizationData?.organizationId);
      formData.append(
        `assessment_date`,
        assessmentDate ? moment(assessmentDate)?.format("DD-MM-YYYY") : null,
      );

      formData.append(
        `year`,
        year ? moment(year, "YYYY")?.format("YYYY") : null,
      );

      // formData.append(
      //   `next_review_date`,
      //   reviewDate ? moment(reviewDate)?.format("DD-MM-YYYY") : null
      // );
      formData.append(
        `compliance_manager_id`,
        complianceManagerId ? complianceManagerId : "",
      );
      formData.append(`section_type`, sectionType);

      if (id) {
        formData.append("_method", "PUT");
      }

      sections.forEach((section, sectionIndex) => {
        formData.append(`sections[${sectionIndex}][section_id]`, section.id);
        section.questions.forEach((question, questionIndex) => {
          formData.append(
            `sections[${sectionIndex}][questions][${questionIndex}][id]`,
            question.id,
          );
          formData.append(
            `sections[${sectionIndex}][questions][${questionIndex}][answer]`,
            question.answer
              ? question.answer === "Yes"
                ? 1
                : question.answer === "No"
                  ? 0
                  : ""
              : "",
          );
        });
      });

      const { data } = await fileUpload(
        id ? `${API.GET_RISK_ASSESSMENT}/${id}` : API.GET_RISK_ASSESSMENT,
        formData,
      );
      toggleShowAddEditRisk();
      fetchRiskData();
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const getMissingQuestions = (sectionIndex) => {
    return sections[sectionIndex]?.questions
      .map((q, index) => (q.answer === null ? index : null))
      .filter((i) => i !== null);
  };

  return (
    <>
      {loading && <Spin expand={true} />}
      <div className="flex items-center w-full justify-center">
        <div className="w-full">
          <h5 className="mb-4 font-semibold">
            {sectionType} Risk Assessment for{" "}
            {organizationData?.organizationData?.business_name}
          </h5>
          <div>
            {/* <div className="grid grid-cols-5 gap-4 my-4"> */}
            <div className="flex items-center gap-4 my-4 justify-start">
              <div className="grid gap-2">
                <Label htmlFor="year">
                  <CustomLabelText required={true} labelText="Year" />
                </Label>
                <YearPicker
                  className={"min-w-[118px]"}
                  onChange={(year) => setYear(year)}
                  value={year}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="file_note">
                  <CustomLabelText
                    required={true}
                    labelText="Assessment Date"
                  />
                </Label>
                <DateInputPicker
                  date={assessmentDate}
                  onSelect={(e) => {
                    setAssessmentDate(e);
                  }}
                  className={"w-[200px] text-left"}
                  inputClassName="w-full"
                  icon={true}
                  disabledDate={{ after: new Date() }}
                />
              </div>
              {/*  <div className="grid gap-2">
                <Label htmlFor="file_note">
                  <CustomLabelText
                    required={true}
                    labelText="Next Review Date"
                  />
                </Label>
                <DateInputPicker
                  date={reviewDate}
                  onSelect={(e) => {
                    setReviewDate(e);
                  }}
                  className={"w-[200px] text-left"}
                  inputClassName="w-full"
                  icon={true}
                />
              </div> */}
              <div className="grid gap-2">
                <div className="min-w-0">
                  <Label htmlFor="compliance_manager_id">
                    <CustomLabelText
                      required={true}
                      labelText="Compliance Manager"
                    />
                  </Label>
                  <div className="relative mt-2">
                    <UserSelect
                      orgId={organizationData?.organizationId}
                      frameworkData={userListing} // List of vendors
                      optionType="User"
                      handleSelect={(e) => {
                        setComplianceManagerId(e);
                      }}
                      name="compliance_manager_id"
                      disabled={false}
                      initialValue={
                        complianceManagerId ? complianceManagerId : ""
                      }
                      setUserList={setUserList}
                      setUserLoading={setUserLoading}
                      onClear={() => {
                        setComplianceManagerId("");
                      }}
                      compliance={true}
                      loading={userLoading}
                    />
                    {userLoading && (
                      <span className="absolute top-2 right-2 animate-spin">
                        <Loader2 width={20} height={20} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid gap-2 justify-start">
                <Label htmlFor="file_note">
                  <CustomLabelText labelText="Assessed By" />
                </Label>
                <p className="my-1">
                  {assessedBy
                    ? assessedBy
                    : organizationData?.organizationData?.business_name}
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-stretch flex-col gap-4">
            <Stepper
              initialStep={0}
              steps={stepsData}
              onClickStep={(step, setStep) => setStep(step)}
            >
              {sections?.map((section, index) => {
                const missingQuestions = getMissingQuestions(index);
                const missingTooltipContent = (
                  <div>
                    Question No.
                    {missingQuestions?.map((index) => (
                      <div key={index}>Q{index + 1}</div>
                    ))}
                  </div>
                );
                return (
                  <Step
                    key={section.section}
                    label={section.title}
                    horizontalStepClassName={"flex flex-col gap-2"}
                  >
                    <RiskStepContent
                      section={section}
                      sectionIndex={index}
                      sections={sections}
                      setSections={setSections}
                      fetchAnalysis={fetchAnalysis}
                      missingQuestions={missingQuestions}
                      missingTooltipContent={missingTooltipContent}
                    />
                  </Step>
                );
              })}

              {/* Final summary step */}
              <Step
                label="Summary"
                horizontalStepClassName={"flex flex-col gap-2"}
              >
                <SummaryStep
                  sections={sections}
                  setLoading={setLoading}
                  analysis={analysis}
                  sectionType={sectionType}
                  handleFinish={handleFinish}
                  fetchAnalysis={fetchAnalysis}
                  disableSubmit={
                    year &&
                    assessmentDate &&
                    // reviewDate &&
                    complianceManagerId &&
                    analysis
                      ? false
                      : true
                  }
                />
              </Step>
            </Stepper>
          </div>
        </div>
      </div>
    </>
  );
}
